import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Bell, Eye, EyeOff, FileText, GripVertical, Hash, Link2, Trash2, Zap } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { ColorSwatchPopover } from './color-swatch-popover';
import { PATIENT_DISPLAY_STYLES, PATIENT_ICONS } from '@/lib/queues';
import { cn } from '@/lib/utils';

// Indicator for the sub-category's transition events: the first non-empty trigger (onEnter wins,
// then onCheckIn, then onExit), with a "+N" when several keys are set. Edited in the
// Transition events tab — read-only here.
function eventSummary(events) {
  for (const trigger of ['onEnter', 'onCheckIn', 'onExit']) {
    const keys = events?.[trigger];
    if (Array.isArray(keys) && keys.length > 0) {
      return { trigger, first: keys[0], extra: keys.length - 1 };
    }
  }
  return null;
}

const selectClass =
  'h-8 rounded-md border border-input bg-background px-2 text-xs font-medium text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50';

export function SubCategoryRow({ sub, usedColorIds, queueNameById, disabled = false, onChange, onDelete }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: sub.id,
  });
  const summary = eventSummary(sub.events);
  // Queue-link chip (edited in the Queue links tab — read-only here, like the events chip).
  const linkedQueueName = sub.linkedQueue ? queueNameById?.get(sub.linkedQueue.queueId) : null;
  const showToPatient = Boolean(sub.showToPatient);
  const showVisitSummary = Boolean(sub.showVisitSummary);
  const showQueuePosition = sub.showQueuePosition !== false; // default: shown
  const notifyPatient = Boolean(sub.notifyPatient); // default: silent

  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={cn(
        'flex flex-col gap-1.5 rounded-lg border border-border bg-background px-2 py-1.5',
        isDragging ? 'opacity-60 shadow-md ring-2 ring-ring/50' : '',
      )}
    >
      <div className="flex items-center gap-2.5">
        <button
          type="button"
          aria-label="Drag to reorder sub-category"
          {...attributes}
          {...listeners}
          className="shrink-0 cursor-grab rounded text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <GripVertical className="h-4 w-4" aria-hidden />
        </button>
        <ColorSwatchPopover
          colorId={sub.colorId}
          onChange={(c) => onChange({ colorId: c })}
          usedColorIds={usedColorIds}
          disabled={disabled}
          label="Sub-category colour"
        />
        <Input
          value={sub.name}
          onChange={(e) => onChange({ name: e.target.value })}
          maxLength={60}
          disabled={disabled}
          className="h-8 w-48 text-sm"
          placeholder="Sub-category"
        />
        <Input
          value={sub.description}
          onChange={(e) => onChange({ description: e.target.value })}
          maxLength={120}
          disabled={disabled}
          className="h-8 flex-1 text-sm"
          placeholder="Description (shown to staff)"
        />
        {sub.linkedQueue ? (
          <span
            className={cn(
              'inline-flex shrink-0 items-center gap-1.5 rounded-md px-2 py-1 text-[11.5px] font-bold',
              linkedQueueName
                ? 'bg-sky-500/15 text-sky-600 ring-1 ring-sky-500/40 dark:text-sky-400'
                : 'bg-amber-500/15 text-amber-600 ring-1 ring-amber-500/40 dark:text-amber-400',
            )}
            title="Linked queue — edited in the Queue links tab"
          >
            <Link2 className="h-3 w-3" aria-hidden />
            {sub.linkedQueue.mode === 'mirror' ? 'Mirror' : 'Move'} · {linkedQueueName || 'missing queue'}
          </span>
        ) : null}
        {summary ? (
          <span className="inline-flex shrink-0 items-center gap-1.5 rounded-md bg-primary/15 px-2 py-1 font-mono text-[11.5px] font-bold text-primary ring-1 ring-primary/40">
            <Zap className="h-3 w-3" aria-hidden />
            {summary.trigger} · {summary.first}
            {summary.extra > 0 ? ` +${summary.extra}` : ''}
          </span>
        ) : (
          <span className="inline-flex shrink-0 items-center gap-1.5 px-2 py-1 text-[11.5px] font-medium text-muted-foreground">
            <Zap className="h-3 w-3" aria-hidden />
            No events
          </span>
        )}
        <button
          type="button"
          onClick={onDelete}
          disabled={disabled}
          aria-label={`Delete sub-category ${sub.name}`}
          className={cn(
            'inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-transparent text-muted-foreground transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            disabled
              ? 'cursor-not-allowed opacity-50'
              : 'hover:border-destructive/40 hover:bg-destructive/10 hover:text-destructive',
          )}
        >
          <Trash2 className="h-3.5 w-3.5" aria-hidden />
        </button>
      </div>

      {/* Patient app row: whether the patient perceives this status, and how it renders. Wraps so the
          per-status toggles never run off the edge on narrower windows. */}
      <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1.5 pl-[26px]">
        <button
          type="button"
          onClick={() => onChange({ showToPatient: !showToPatient })}
          disabled={disabled}
          title="Show this status on the patient app (My Visit)"
          className={cn(
            'inline-flex shrink-0 items-center gap-1.5 rounded-md px-2 py-1 text-[11.5px] font-semibold transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50',
            showToPatient
              ? 'bg-emerald-500/15 text-emerald-600 ring-1 ring-emerald-500/40 dark:text-emerald-400'
              : 'text-muted-foreground hover:bg-muted',
          )}
        >
          {showToPatient ? <Eye className="h-3 w-3" aria-hidden /> : <EyeOff className="h-3 w-3" aria-hidden />}
          {showToPatient ? 'Patient sees' : 'Hidden from patient'}
        </button>

        {showToPatient ? (
          <>
            <label className="flex items-center gap-1.5 text-[11px] font-medium text-muted-foreground">
              Style
              <select
                value={sub.patientDisplayStyle || 'action'}
                onChange={(e) => onChange({ patientDisplayStyle: e.target.value })}
                disabled={disabled}
                className={selectClass}
                aria-label="Patient display style"
              >
                {PATIENT_DISPLAY_STYLES.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex items-center gap-1.5 text-[11px] font-medium text-muted-foreground">
              Icon
              <select
                value={sub.patientIcon || ''}
                onChange={(e) => onChange({ patientIcon: e.target.value })}
                disabled={disabled}
                className={selectClass}
                aria-label="Patient icon"
              >
                {PATIENT_ICONS.map((icon) => (
                  <option key={icon || 'none'} value={icon}>
                    {icon === '' ? 'No icon' : icon}
                  </option>
                ))}
              </select>
            </label>
            <Input
              value={sub.room || ''}
              onChange={(e) => onChange({ room: e.target.value })}
              maxLength={40}
              disabled={disabled}
              className="h-8 w-40 text-sm"
              placeholder="Room (optional)"
            />
            {/* Behaviour toggles — kept together so they wrap as a pair, not split across lines. */}
            <div className="flex items-center gap-2.5">
              <button
                type="button"
                onClick={() => onChange({ showQueuePosition: !showQueuePosition })}
                disabled={disabled}
                title="Show the queue order (place in line, #N) on this status"
                className={cn(
                  'inline-flex shrink-0 items-center gap-1.5 rounded-md px-2 py-1 text-[11.5px] font-semibold transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50',
                  showQueuePosition
                    ? 'bg-emerald-500/15 text-emerald-600 ring-1 ring-emerald-500/40 dark:text-emerald-400'
                    : 'text-muted-foreground hover:bg-muted',
                )}
              >
                <Hash className="h-3 w-3" aria-hidden />
                Queue order
              </button>
              <button
                type="button"
                onClick={() => onChange({ showVisitSummary: !showVisitSummary })}
                disabled={disabled}
                title="The visit-done status: show the 'open visit summary' button and hide the queue position on the patient app"
                className={cn(
                  'inline-flex shrink-0 items-center gap-1.5 rounded-md px-2 py-1 text-[11.5px] font-semibold transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50',
                  showVisitSummary
                    ? 'bg-primary/15 text-primary ring-1 ring-primary/40'
                    : 'text-muted-foreground hover:bg-muted',
                )}
              >
                <FileText className="h-3 w-3" aria-hidden />
                Visit summary
              </button>
              <button
                type="button"
                onClick={() => onChange({ notifyPatient: !notifyPatient })}
                disabled={disabled}
                title="Send the patient a push notification when they are moved into this status"
                className={cn(
                  'inline-flex shrink-0 items-center gap-1.5 rounded-md px-2 py-1 text-[11.5px] font-semibold transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50',
                  notifyPatient
                    ? 'bg-amber-500/15 text-amber-600 ring-1 ring-amber-500/40 dark:text-amber-400'
                    : 'text-muted-foreground hover:bg-muted',
                )}
              >
                <Bell className="h-3 w-3" aria-hidden />
                Notify
              </button>
            </div>
          </>
        ) : (
          <span className="text-[11px] text-muted-foreground/70">
            Patients on this status keep seeing their last visible one.
          </span>
        )}
      </div>
    </div>
  );
}
